/**
 * Tema claro / oscuro / sistema.
 *
 * La clase `dark` se aplica en <html> y todo lo demas sale de las variables CSS
 * de `index.css`. El primer pintado ya lo resuelve el script inline de
 * `index.html`; este modulo mantiene la coherencia despues de la carga.
 *
 * Es un store y no un `useState` por componente: el tema se puede cambiar desde
 * el boton de la barra superior y tambien desde la paleta de comandos, y ambos
 * tienen que mostrar el mismo estado sin pasarse props.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'nucleo.theme';
const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

function leerPreferencia(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  try {
    const valor = localStorage.getItem(STORAGE_KEY);
    return valor === 'light' || valor === 'dark' || valor === 'system' ? valor : 'system';
  } catch {
    return 'system';
  }
}

function prefiereOscuroElSistema(): boolean {
  return typeof matchMedia === 'function' && matchMedia(CONSULTA_OSCURO).matches;
}

function resolver(preferencia: ThemePreference): 'light' | 'dark' {
  return preferencia === 'system' ? (prefiereOscuroElSistema() ? 'dark' : 'light') : preferencia;
}

function aplicar(preferencia: ThemePreference): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolver(preferencia) === 'dark');
}

function persistir(preferencia: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preferencia);
  } catch {
    // Storage bloqueado: el tema dura lo que la pestana.
  }
}

interface ThemeState {
  preference: ThemePreference;
  /** Tema efectivo, ya resuelto contra el sistema operativo. */
  resolved: 'light' | 'dark';
  setPreference: (preferencia: ThemePreference) => void;
  /** Ciclo del boton: claro -> oscuro -> segun el sistema. */
  cycle: () => void;
  /** Recalcula el tema efectivo cuando el sistema cambia solo. */
  syncWithSystem: () => void;
}

const INICIAL = leerPreferencia();

export const useThemeStore = create<ThemeState>()((set, get) => ({
  preference: INICIAL,
  resolved: resolver(INICIAL),

  setPreference: (preferencia) => {
    aplicar(preferencia);
    persistir(preferencia);
    set({ preference: preferencia, resolved: resolver(preferencia) });
  },

  cycle: () => {
    const actual = get().preference;
    const siguiente: ThemePreference = actual === 'light' ? 'dark' : actual === 'dark' ? 'system' : 'light';
    get().setPreference(siguiente);
  },

  syncWithSystem: () => {
    if (get().preference !== 'system') return;
    aplicar('system');
    set({ resolved: resolver('system') });
  },
}));

/**
 * Acceso al tema desde un componente. El efecto se suscribe al cambio de tema
 * del sistema operativo, que solo importa cuando la preferencia es `system`.
 */
export function useTheme() {
  const preference = useThemeStore((estado) => estado.preference);
  const resolved = useThemeStore((estado) => estado.resolved);
  const setPreference = useThemeStore((estado) => estado.setPreference);
  const cycle = useThemeStore((estado) => estado.cycle);
  const syncWithSystem = useThemeStore((estado) => estado.syncWithSystem);

  useEffect(() => {
    if (preference !== 'system' || typeof matchMedia !== 'function') return;
    const medio = matchMedia(CONSULTA_OSCURO);
    medio.addEventListener('change', syncWithSystem);
    return () => {
      medio.removeEventListener('change', syncWithSystem);
    };
  }, [preference, syncWithSystem]);

  return { preference, resolved, setPreference, cycle };
}

export const ETIQUETAS_TEMA: Record<ThemePreference, string> = {
  light: 'Tema claro',
  dark: 'Tema oscuro',
  system: 'Según el sistema',
};
